import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Tag, Plus, Trash2, Percent, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import EventSubNav from '../../components/EventSubNav';
import { eventsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const EventDiscountsPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: null
  });

  useEffect(() => {
    fetchData();
  }, [eventId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventRes, codesRes] = await Promise.all([
        eventsApi.getById(eventId),
        eventsApi.getDiscountCodes(eventId),
      ]);
      setEvent(eventRes.data);
      setDiscountCodes(codesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar códigos de descuento');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.code.trim()) {
      toast.error('Ingresa un código');
      return;
    }
    try {
      await eventsApi.createDiscountCode(eventId, {
        ...formData,
        event_id: eventId
      });
      toast.success('Código de descuento creado');
      setShowModal(false);
      setFormData({ code: '', discount_type: 'percentage', discount_value: 10, max_uses: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear código');
    }
  };

  const handleToggle = async (codeId, isActive) => {
    try {
      await eventsApi.toggleDiscountCode(eventId, codeId, !isActive);
      toast.success('Código actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar código');
    }
  };

  const handleDelete = async (codeId) => {
    if (!confirm('¿Eliminar este código de descuento?')) return;
    try {
      await eventsApi.deleteDiscountCode(eventId, codeId);
      toast.success('Código eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar código');
    }
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#1A1A1A] w-1/3 rounded" />
            <div className="h-64 bg-[#1A1A1A] rounded" />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  const activeCount = discountCodes.filter(c => c.is_active).length;
  const totalUses = discountCodes.reduce((sum, c) => sum + (c.times_used || 0), 0);

  return (
    <SidebarProvider>
      <AdminLayout>
        <div data-testid="event-discounts-page">
          <EventSubNav eventTitle={event?.title} />

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-6 mb-8"
          >
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Total Códigos</p>
              <p className="text-3xl font-black">{discountCodes.length}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Códigos Activos</p>
              <p className="text-3xl font-black text-green-500">{activeCount}</p>
            </div>
            <div className="card-dashboard">
              <p className="text-[#A1A1AA] text-sm uppercase tracking-wider mb-1">Veces Usado</p>
              <p className="text-3xl font-black">{totalUses}</p>
            </div>
          </motion.div>

          {/* Header Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between mb-6"
          >
            <h2 className="text-xl font-bold uppercase tracking-tight">Códigos de Descuento</h2>
            <Button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={16} className="mr-2" />
              Nuevo Código
            </Button>
          </motion.div>

          {/* Codes List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            {discountCodes.length > 0 ? (
              discountCodes.map((code) => (
                <div 
                  key={code.id} 
                  className={`card-dashboard flex items-center justify-between ${!code.is_active && 'opacity-50'}`}
                >
                  <div className="flex items-center gap-6">
                    <div className={`p-3 ${code.is_active ? 'bg-green-500/20' : 'bg-[#27272A]'}`}>
                      <Tag size={24} className={code.is_active ? 'text-green-500' : 'text-[#52525B]'} />
                    </div>
                    <div>
                      <p className="font-mono font-black text-2xl tracking-wider">{code.code}</p>
                      <div className="flex items-center gap-4 mt-1 text-[#A1A1AA] text-sm">
                        <span className="flex items-center gap-1">
                          {code.discount_type === 'percentage' ? (
                            <><Percent size={14} />{code.discount_value}% off</>
                          ) : (
                            <><DollarSign size={14} />${code.discount_value} off</>
                          )}
                        </span>
                        {code.max_uses && (
                          <span>Máx {code.max_uses} usos</span>
                        )}
                        <span>{code.times_used} veces usado</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#A1A1AA]">{code.is_active ? 'Activo' : 'Inactivo'}</span>
                      <Switch 
                        checked={code.is_active} 
                        onCheckedChange={() => handleToggle(code.id, code.is_active)}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(code.id)}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="card-dashboard text-center py-16">
                <Tag size={48} className="mx-auto mb-4 text-[#27272A]" />
                <p className="text-[#52525B] text-lg">No hay códigos de descuento</p>
                <p className="text-[#52525B] text-sm mt-2">Crea un código para ofrecer descuentos a tus clientes</p>
                <Button onClick={() => setShowModal(true)} className="btn-secondary mt-6">
                  <Plus size={16} className="mr-2" />
                  Crear Primer Código
                </Button>
              </div>
            )}
          </motion.div>
        </div>

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#27272A] p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold uppercase mb-6">Nuevo Código de Descuento</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Código</Label>
                <input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="DESCUENTO20"
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none font-mono text-lg"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Tipo</Label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] py-3 px-3 text-white outline-none"
                  >
                    <option value="percentage">Porcentaje (%)</option>
                    <option value="fixed">Monto Fijo ($)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Valor</Label>
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({...formData, discount_value: parseFloat(e.target.value) || 0})}
                    className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2 block">Máximo de Usos (opcional)</Label>
                <input
                  type="number"
                  value={formData.max_uses || ''}
                  onChange={(e) => setFormData({...formData, max_uses: e.target.value ? parseInt(e.target.value) : null})}
                  placeholder="Sin límite"
                  className="w-full bg-transparent border-b border-[#27272A] focus:border-white px-0 py-3 text-white outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-8">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-[#27272A]">
                Cancelar
              </Button>
              <Button onClick={handleCreate} className="flex-1 btn-primary">
                Crear Código
              </Button>
            </div>
          </motion.div>
        </div>
      )}
        </AdminLayout>
      </SidebarProvider>
  );
};

export default EventDiscountsPage;
